import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';

import { AnyExercise } from '@cf-shared/data-model/exercise';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { Log_Workout, Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordType } from '@cf-shared/data-model/user-log';
import { DeclaredState, FitnessState } from '@cf-shared/data-model/user-state';
import { LL } from 'i18n/i18n.service';
import { DocWatcher } from 'services/database-wrapper';
import { DebugService } from 'services/debug.service';
import { ExerciseDbService, getExerciseName } from 'services/exercise-db.service';
import { resolveAnyExerciseByIdAsync } from 'services/exercise-resolve';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { WorkoutLoaderService } from 'services/workout-loader.service';
import { planRepTargetShortText, planSetCount } from 'util/exercise-plan-texts';
import { formatExerciseSets, Formatting } from 'util/formatting';
import {
  OverviewRow,
  OverviewSessionInput,
  TRAINING_OVERVIEW_NO_VALUE,
  TrainingOverviewModel,
  buildTrainingOverview,
  cellTexts,
  readWorkoutsCheckingEmptyAsync,
} from './training-overview';

export interface TrainingOverviewLoadResult {
  model: TrainingOverviewModel;
  /**
   * True when a finished workout older than the loaded window is on this device. Always false
   * while older logs are still downloading: the window is never widened then (see `loadAsync`).
   */
  mayHaveOlder: boolean;
  /** true while the logs older than the first 3 months are still downloading to this device */
  olderLogsDownloading: boolean;
}

/**
 * Loads what the Training Overview needs and builds its model (#5456, phase 3): the program, the
 * logged workouts of the last weeks, and each row's name, sets count and rep target.
 *
 * It reads only. Every host uses it: the Training Overview page, the Training tab on the coach's
 * check-up review, and the row in a person's own check-up. The rules are in `training-overview.ts`.
 * See design-docs/coaching-dashboard.md, "Phase 3: the Training Overview".
 */
@Injectable({ providedIn: 'root' })
export class TrainingOverviewDataService {
  constructor(
    private userDb: UserDb,
    private exerciseDb: ExerciseDbService,
    private workoutLoader: WorkoutLoaderService,
    private settings: SettingsService,
    private debug: DebugService,
  ) {}

  /**
   * Builds the overview from the last `windowDays` days of logs, showing the newest
   * `shownSessionCount(planReference)` sessions of each table.
   *
   * Errors travel up: the host shows an error state, so "no sessions" and "could not look" stay
   * different.
   *
   * The caller must not ask for more than `TRAINING_OVERVIEW_WINDOW_DAYS` while `mayHaveOlder` is
   * false. While older logs are still downloading, a read that starts before the first 3 months
   * pulls only the 7 days around its start date from the server and waits on the network, which
   * would leave a hole in the middle of the grid.
   */
  async loadAsync(
    watcher: DocWatcher,
    windowDays: number,
    shownSessionCount: (planReference: number | undefined) => number,
  ): Promise<TrainingOverviewLoadResult> {
    const now = this.debug.getNowLocal();
    const windowStart = now.startOf('day').minus({ days: windowDays });
    const windowEnd = now.startOf('day').plus({ days: 1 }).endOf('day');

    const plan = await this.userDb.loadPlanAsync(watcher);
    const fitnessState = await this.userDb.loadFitnessStateAsync(watcher);
    const declaredState = await this.userDb.loadDeclaredStateAsync(watcher);

    const workouts = await this.readWorkoutsAsync(windowStart, windowEnd, watcher);
    const sessions: OverviewSessionInput[] = workouts.map(workout => {
      // a workout stored without its time zone offset (an old or damaged document) is read in the
      // device's zone, so it still gets a calendar day and a column
      const localTime = Number.isFinite(workout.timeOffset_min)
        ? getEntryLocalTimeInDesiredTimeZone(workout, now)
        : DateTime.fromISO(workout.time_utc).setZone(now.zone);
      return {
        workout,
        planReference: this.workoutLoader.getEffectivePlanReference(workout, plan),
        dateIso: localTime.toISODate(),
        timeMs: DateTime.fromISO(workout.time_utc).toMillis(),
      };
    });

    const locale = this.settings.locale;
    const model = buildTrainingOverview({
      plan,
      sessions,
      shownSessionCount,
      formatCell: (entry, isSameWeight) => cellTexts(formatExerciseSets(entry as Log_WorkoutExercise, locale, true, false), isSameWeight, Formatting.times),
    });
    await this.fillRowTextsAsync(model, plan, declaredState, fitnessState, watcher);

    const olderLogsDownloading = !this.userDb.database.isBackgroundSyncComplete();
    let mayHaveOlder = false;
    // if every log is on the device, ask whether a finished workout lies before the window
    if (!olderLogsDownloading) {
      const oldestTime = await this.userDb.getOldestCompletedWorkoutTimeAsync();
      const oldest = oldestTime ? DateTime.fromISO(oldestTime) : undefined;
      mayHaveOlder = !!oldest?.isValid && oldest < windowStart;
    }
    return { model, mayHaveOlder, olderLogsDownloading };
  }

  /**
   * The workouts logged in the range. An empty answer is checked once against the latest finished
   * workout, because the cache behind the range read can answer "nothing" when it was cleared
   * mid-read; see `TrainingOverviewEmptyReadError`.
   */
  private async readWorkoutsAsync(start: DateTime, end: DateTime, watcher: DocWatcher): Promise<Log_Workout[]> {
    const readRange = async (): Promise<Log_Workout[]> =>
      (await this.userDb.getLogEntriesInRangeAsync(start, end, watcher))
        .filter(entry => entry?.type === LogRecordType.Workout) as Log_Workout[];
    const latestFinishedTimeMs = async (): Promise<number | undefined> => {
      const latest = await this.userDb.getLatestFinishedWorkoutAsync();
      const time = latest?.time_utc ? DateTime.fromISO(latest.time_utc) : undefined;
      return time?.isValid ? time.toMillis() : undefined;
    };
    return await readWorkoutsCheckingEmptyAsync(readRange, latestFinishedTimeMs, start.toMillis(), end.toMillis());
  }

  /** Fills in what only a loader can look up: each row's name, sets count and rep target, and each section's title. */
  private async fillRowTextsAsync(
    model: TrainingOverviewModel,
    plan: FitnessPlan,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
    watcher: DocWatcher,
  ): Promise<void> {
    // each exercise is looked up once, however many rows show it; undefined means it no longer exists
    const exerciseById = new Map<string, AnyExercise | undefined>();
    const resolveAsync = async (exerciseId: string): Promise<AnyExercise | undefined> => {
      if (!exerciseById.has(exerciseId)) {
        exerciseById.set(exerciseId, await this.resolveExerciseAsync(exerciseId, watcher));
      }
      return exerciseById.get(exerciseId);
    };

    for (const section of model.sections) {
      for (const row of [...section.programRows, ...section.greyRows]) {
        const exercise = await resolveAsync(row.exerciseId);
        row.isExerciseMissing = !exercise;
        row.name = exercise ? getExerciseName(exercise) : LL.trainingOverview.deletedExercise();
        if (row.isInProgram) {
          this.fillProgramTexts(row, exercise, plan, declaredState, fitnessState);
        }
      }
      const workout = section.planReference === undefined ? undefined : plan.workouts?.[section.planReference];
      section.badge = workout?.name ?? '';
      section.title = workout ? LL.training.trainingProgram.workoutLetter({ letter: workout.name }) : LL.trainingOverview.otherSessions();
      section.exerciseNamesText = section.programRows.map(row => row.name).join(', ');
    }
  }

  /** the sets count and rep target of a program row, as Edit Workouts shows them for the same exercise */
  private fillProgramTexts(
    row: OverviewRow,
    exercise: AnyExercise | undefined,
    plan: FitnessPlan,
    declaredState: DeclaredState,
    fitnessState: FitnessState,
  ): void {
    const subSlot = row.subSlotId ? plan.subSlots?.[row.subSlotId] : undefined;
    // if the exercise no longer exists, its targets cannot be worked out: both read "–"
    if (!exercise || !subSlot) {
      row.setsText = TRAINING_OVERVIEW_NO_VALUE;
      row.repTargetText = TRAINING_OVERVIEW_NO_VALUE;
      return;
    }
    row.setsText = String(planSetCount(subSlot, exercise, declaredState, fitnessState));
    row.repTargetText = planRepTargetShortText(subSlot, exercise, declaredState, fitnessState) ?? TRAINING_OVERVIEW_NO_VALUE;
  }

  /**
   * The exercise an id names, from the curated catalog or the user's own exercises, or undefined
   * when it no longer exists. A deleted custom exercise arrives as a thrown PouchDB not-found, and
   * only that means "gone": any other error (a database that could not be read, or a catalog read
   * that failed, df-7b992e109846) travels up, and the overview shows that it could not load.
   */
  private async resolveExerciseAsync(exerciseId: string, watcher: DocWatcher): Promise<AnyExercise | undefined> {
    try {
      return await resolveAnyExerciseByIdAsync(exerciseId, this.exerciseDb, this.userDb, watcher);
    } catch (err) {
      if (isPouchNotFound(err)) {
        return undefined;
      }
      throw err;
    }
  }
}
